import { useIsFocused, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { HoldingsListItem, InvestmentTransactionItem } from '@perdiem/trpc';
import { trpc } from '../../lib/trpc';
import {
  Badge,
  Card,
  ConnectionErrorBanner,
  EmptyState,
  ErrorState,
  LoadingSpinner,
  Money,
  PageContainer,
  PageHeader,
  SectionHeader,
  SegmentedControl,
  Select,
  TextInput,
  Button,
  WebHead,
} from '../../components';
import { LinearGradient } from 'expo-linear-gradient';
import { useColors } from '../../lib/colors';
import { webPrivacyProps } from '../../lib/privacy';
import {
  formatCurrency,
  formatCurrencyChange,
  formatPercent,
  formatRelativeTime,
  formatSharePrice,
  formatSignedPercent,
  toTitleCase,
} from '../../lib/utils';
import {
  aggregatePortfolioHoldingsBySymbol,
  filterDailyMarketHoldings,
  sortPortfolioHoldings,
  type PortfolioHoldingSortMode,
  type PortfolioHoldingSymbolAggregate,
} from '../../lib/portfolio-holdings';

type TopTab = 'holdings' | 'activity';
type SortMode = PortfolioHoldingSortMode;
type GroupMode = 'none' | 'account' | 'symbol';

const TAB_OPTIONS = [
  { label: 'Holdings', value: 'holdings' },
  { label: 'Activity', value: 'activity' },
] as const;

const SORT_OPTIONS = [
  { label: 'Largest', value: 'largest' },
  { label: 'Movers', value: 'movers' },
] as const;

const GROUP_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Account', value: 'account' },
  { label: 'Symbol', value: 'symbol' },
] as const;

const ACTIVITY_TYPE_OPTIONS = [
  { label: 'All types', value: 'all' },
  { label: 'Buy', value: 'buy' },
  { label: 'Sell', value: 'sell' },
  { label: 'Cash', value: 'cash' },
  { label: 'Transfer', value: 'transfer' },
  { label: 'Fee', value: 'fee' },
] as const;

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: value >= 100 ? 0 : 4,
  }).format(value);
}

function getChangeTone(value: number | null): string {
  if (value == null) return 'text-gray-500 dark:text-gray-400';
  if (value > 0) return 'text-green-600 dark:text-green-500';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

function getPriceStatusMeta(status: HoldingsListItem['priceStatus']): { label: string; variant: 'blue' | 'gray' | 'yellow' } | null {
  if (status === 'current') return null;
  if (status === 'close') return { label: 'Delayed close', variant: 'blue' };
  if (status === 'reported') return { label: 'Institution price', variant: 'yellow' };
  return { label: 'Price unavailable', variant: 'gray' };
}

function HoldingRow({ item, hideAccount = false }: { item: HoldingsListItem; hideAccount?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [basisOpen, setBasisOpen] = useState(false);
  const [basisQuantity, setBasisQuantity] = useState(String(item.quantity));
  const [basisAmount, setBasisAmount] = useState('');
  const [acquiredAt, setAcquiredAt] = useState('2020-01-01');
  const utils = trpc.useUtils();
  const setBasis = trpc.holdings.setOpeningBasis.useMutation({
    onSuccess: async () => {
      setBasisOpen(false);
      setBasisAmount('');
      await utils.holdings.list.invalidate();
    },
  });
  const priceStatus = getPriceStatusMeta(item.priceStatus);
  const dayChangeLabel = item.dayChangeAmount != null ? formatCurrencyChange(item.dayChangeAmount) : null;
  const dayChangePercentLabel = formatSignedPercent(item.dayChangePercent);
  const gainLossLabel = item.gainLossAmount != null ? formatCurrencyChange(item.gainLossAmount) : null;
  const gainLossPercentLabel = formatSignedPercent(item.gainLossPercent);

  return (
    <Pressable
      onPress={() => setExpanded((v) => !v)}
      className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
    >
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1 gap-1">
          <View className="flex-row items-center gap-2 flex-wrap">
            <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {item.symbol ?? item.name}
            </Text>
            {priceStatus && <Badge label={priceStatus.label} variant={priceStatus.variant} />}
            {item.basisStatus === 'missing' && <Badge label="Basis missing" variant="yellow" />}
          </View>
          {item.symbol && (
            <Text className="text-sm text-gray-600 dark:text-gray-400" numberOfLines={1}>
              {item.name}
            </Text>
          )}
          <Text className="text-xs text-gray-500" {...webPrivacyProps(['private', 'financial'])}>
            {hideAccount ? '' : `${item.accountName} · `}
            {formatQuantity(item.quantity)} shares
            {item.pricePerShare != null ? ` · ${formatSharePrice(item.pricePerShare)}/sh` : ''}
            {item.allocationPercent != null ? ` · ${formatPercent(item.allocationPercent)} of portfolio` : ''}
          </Text>
        </View>
        <View className="items-end gap-1">
          <Money weight={500} className="text-base text-gray-900 dark:text-gray-100">
            {item.value != null ? formatCurrency(item.value, { decimals: 0 }) : '—'}
          </Money>
          {dayChangeLabel && dayChangePercentLabel ? (
            <Money className={`text-sm ${getChangeTone(item.dayChangeAmount)}`}>
              {dayChangeLabel} ({dayChangePercentLabel})
            </Money>
          ) : (
            <Text className={`text-sm font-medium ${getChangeTone(item.dayChangeAmount)}`}>—</Text>
          )}
        </View>
      </View>

      {expanded && (
        <>
          <View className="mt-3 flex-row flex-wrap gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <Text className="text-xs text-gray-500 w-full" {...webPrivacyProps('financial')}>
              {item.currentPriceAsOf
                ? `Price updated ${formatRelativeTime(item.currentPriceAsOf)}`
                : item.closePriceAsOf
                  ? `Close price from ${item.closePriceAsOf}`
                  : `Synced ${formatRelativeTime(item.updatedAt)}`}
            </Text>
          </View>
          <View className="mt-3 flex-row flex-wrap gap-3 rounded-2xl bg-gray-50 dark:bg-gray-700 p-3">
            <View className="min-w-[140px] flex-1">
              <Text className="text-xs uppercase tracking-wide text-gray-400">Cost Basis</Text>
              <Text
                className="mt-1 text-sm font-medium text-gray-800 dark:text-gray-200"
                {...webPrivacyProps('financial')}
              >
                {item.costBasisAmount != null ? formatCurrency(item.costBasisAmount, { decimals: 0 }) : 'Not available'}
              </Text>
            </View>
            <View className="min-w-[140px] flex-1">
              <Text className="text-xs uppercase tracking-wide text-gray-400">Gain / Loss</Text>
              <Text
                className={`mt-1 text-sm font-medium ${getChangeTone(item.gainLossAmount)}`}
                {...webPrivacyProps('financial')}
              >
                {gainLossLabel && gainLossPercentLabel
                  ? `${gainLossLabel} (${gainLossPercentLabel})`
                  : 'Incomplete'}
              </Text>
            </View>
          </View>
          {item.basisStatus === 'missing' && (
            <View className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3 gap-2">
              <Text className="text-sm font-medium text-amber-900 dark:text-amber-200">Cost basis is needed to compute sale gains.</Text>
              {!basisOpen ? (
                <Button size="sm" variant="secondary" onPress={() => setBasisOpen(true)}>Add opening basis</Button>
              ) : (
                <View className="gap-2">
                  <TextInput label="Shares covered" size="sm" value={basisQuantity} onChangeText={setBasisQuantity} keyboardType="decimal-pad" />
                  <TextInput label="Total cost basis" prefix="$" size="sm" value={basisAmount} onChangeText={setBasisAmount} keyboardType="decimal-pad" />
                  <TextInput label="Original purchase date" hint="Use the earliest date you can support." size="sm" value={acquiredAt} onChangeText={setAcquiredAt} />
                  <View className="flex-row gap-2">
                    <Button size="sm" loading={setBasis.isPending} onPress={() => setBasis.mutate({ holdingId: item.id, quantity: Number(basisQuantity), costBasis: Number(basisAmount), acquiredAt })}>Save basis</Button>
                    <Button size="sm" variant="ghost" onPress={() => setBasisOpen(false)}>Cancel</Button>
                  </View>
                </View>
              )}
            </View>
          )}
        </>
      )}
    </Pressable>
  );
}

type SymbolAggregate = PortfolioHoldingSymbolAggregate<HoldingsListItem>;

function SymbolAggregateRow({ agg }: { agg: SymbolAggregate }) {
  const [expanded, setExpanded] = useState(false);
  const priceStatus = getPriceStatusMeta(agg.priceStatus);
  const dayChangeLabel = agg.dayChangeAmount != null ? formatCurrencyChange(agg.dayChangeAmount) : null;
  const dayChangePercentLabel = formatSignedPercent(agg.dayChangePercent);
  const gainLossLabel = agg.totalGainLoss != null ? formatCurrencyChange(agg.totalGainLoss) : null;
  const gainLossPercentLabel = formatSignedPercent(agg.gainLossPercent);
  const acrossLabel = agg.positions.length > 1 ? ` · ${agg.positions.length} accounts` : '';

  return (
    <Pressable
      onPress={() => setExpanded((v) => !v)}
      className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
    >
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1 gap-1">
          <View className="flex-row items-center gap-2 flex-wrap">
            <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {agg.symbol ?? agg.name}
            </Text>
            {priceStatus && <Badge label={priceStatus.label} variant={priceStatus.variant} />}
          </View>
          {agg.symbol && (
            <Text className="text-sm text-gray-600 dark:text-gray-400" numberOfLines={1}>
              {agg.name}
            </Text>
          )}
          <Text className="text-xs text-gray-500" {...webPrivacyProps(['private', 'financial'])}>
            {formatQuantity(agg.totalQuantity)} shares
            {agg.representative.pricePerShare != null ? ` · ${formatSharePrice(agg.representative.pricePerShare)}/sh` : ''}
            {agg.allocationPercent != null ? ` · ${formatPercent(agg.allocationPercent)} of portfolio` : ''}
            {acrossLabel}
          </Text>
        </View>
        <View className="items-end gap-1">
          <Money weight={500} className="text-base text-gray-900 dark:text-gray-100">
            {agg.totalValue != null ? formatCurrency(agg.totalValue, { decimals: 0 }) : '—'}
          </Money>
          {dayChangeLabel && dayChangePercentLabel ? (
            <Money className={`text-sm ${getChangeTone(agg.dayChangeAmount)}`}>
              {dayChangeLabel} ({dayChangePercentLabel})
            </Money>
          ) : (
            <Text className={`text-sm font-medium ${getChangeTone(agg.dayChangeAmount)}`}>—</Text>
          )}
        </View>
      </View>

      {expanded && (
        <View className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 gap-2">
          {gainLossLabel && gainLossPercentLabel && (
            <Text className={`text-xs ${getChangeTone(agg.totalGainLoss)}`} {...webPrivacyProps('financial')}>
              Total gain {gainLossLabel} ({gainLossPercentLabel})
              {agg.totalCostBasis != null ? ` · cost ${formatCurrency(agg.totalCostBasis, { decimals: 0 })}` : ''}
            </Text>
          )}
          {agg.positions.map((pos) => (
            <View key={pos.id} className="flex-row items-center justify-between py-1.5">
              <View className="flex-1 pr-3">
                <Text className="text-sm text-gray-700 dark:text-gray-300" numberOfLines={1} {...webPrivacyProps('private')}>
                  {pos.accountName}
                </Text>
                <Text className="text-xs text-gray-500" {...webPrivacyProps('financial')}>
                  {formatQuantity(pos.quantity)} shares
                </Text>
              </View>
              <Money weight={500} className="text-sm text-gray-800 dark:text-gray-200">
                {pos.value != null ? formatCurrency(pos.value, { decimals: 0 }) : '—'}
              </Money>
            </View>
          ))}
        </View>
      )}
    </Pressable>
  );
}

interface AccountGroup {
  accountId: string;
  accountName: string;
  totalValue: number;
  dayChangeAmount: number | null;
  items: HoldingsListItem[];
}

function groupByAccount(items: HoldingsListItem[]): AccountGroup[] {
  const map = new Map<string, AccountGroup>();
  for (const item of items) {
    let group = map.get(item.accountId);
    if (!group) {
      group = {
        accountId: item.accountId,
        accountName: item.accountName,
        totalValue: 0,
        dayChangeAmount: null,
        items: [],
      };
      map.set(item.accountId, group);
    }
    group.items.push(item);
    if (item.value != null) group.totalValue += item.value;
    if (item.dayChangeAmount != null) {
      group.dayChangeAmount = (group.dayChangeAmount ?? 0) + item.dayChangeAmount;
    }
  }
  return [...map.values()].sort((a, b) => b.totalValue - a.totalValue);
}

function AccountGroupSection({
  group,
  sort,
  defaultExpanded,
}: {
  group: AccountGroup;
  sort: SortMode;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sorted = useMemo(
    () => sortPortfolioHoldings(sort === 'movers' ? filterDailyMarketHoldings(group.items) : group.items, sort),
    [group.items, sort],
  );
  const dayChangeLabel = group.dayChangeAmount != null ? formatCurrencyChange(group.dayChangeAmount) : null;
  const priorValue = group.dayChangeAmount != null ? group.totalValue - group.dayChangeAmount : null;
  const dayChangePercent = priorValue != null && priorValue !== 0 && group.dayChangeAmount != null
    ? group.dayChangeAmount / priorValue
    : null;
  const dayChangePercentLabel = formatSignedPercent(dayChangePercent);

  return (
    <View className="rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <Pressable
        onPress={() => setExpanded((v) => !v)}
        className="flex-row items-center justify-between bg-gray-50 dark:bg-gray-800 px-4 py-3"
      >
        <View className="flex-1 pr-3">
          <View className="flex-row items-center gap-2">
            <Ionicons
              name={expanded ? 'chevron-down' : 'chevron-forward'}
              size={14}
              color="#6B7280"
            />
            <Text
              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
              numberOfLines={1}
              {...webPrivacyProps('private')}
            >
              {group.accountName}
            </Text>
          </View>
          <Text className="text-xs text-gray-500 mt-0.5 ml-5">
            {group.items.length} {group.items.length === 1 ? 'position' : 'positions'}
          </Text>
        </View>
        <View className="items-end">
          <Money weight={500} className="text-sm text-gray-900 dark:text-gray-100">
            {formatCurrency(group.totalValue, { decimals: 0 })}
          </Money>
          {dayChangeLabel && dayChangePercentLabel && (
            <Money className={`text-xs ${getChangeTone(group.dayChangeAmount)}`}>
              {dayChangeLabel} ({dayChangePercentLabel})
            </Money>
          )}
        </View>
      </Pressable>
      {expanded && (
        <View className="bg-white dark:bg-gray-900 p-3 gap-2">
          {sorted.map((item) => (
            <HoldingRow key={item.id} item={item} hideAccount />
          ))}
        </View>
      )}
    </View>
  );
}

function getActivityBadge(type: string, subtype: string): { label: string; variant: 'green' | 'red' | 'blue' | 'gray' | 'yellow' } {
  switch (type) {
    case 'buy':
      return { label: 'Buy', variant: 'red' };
    case 'sell':
      return { label: 'Sell', variant: 'green' };
    case 'cash':
      if (subtype.includes('dividend')) return { label: 'Dividend', variant: 'green' };
      if (subtype.includes('interest')) return { label: 'Interest', variant: 'green' };
      if (subtype.includes('contribution')) return { label: 'Contribution', variant: 'blue' };
      if (subtype.includes('withdrawal')) return { label: 'Withdrawal', variant: 'yellow' };
      return { label: toTitleCase(subtype), variant: 'gray' };
    case 'fee':
      return { label: 'Fee', variant: 'red' };
    case 'transfer':
      return { label: 'Transfer', variant: 'blue' };
    case 'cancel':
      return { label: 'Cancelled', variant: 'gray' };
    default:
      return { label: toTitleCase(subtype || type), variant: 'gray' };
  }
}

function ActivityRow({ item }: { item: InvestmentTransactionItem }) {
  const badge = getActivityBadge(item.type, item.subtype);
  const isInflow = item.amount < 0;

  return (
    <View className="flex-row items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <View className="flex-1 gap-1 pr-3">
        <View className="flex-row items-center gap-2">
          <Badge label={badge.label} variant={badge.variant} />
          {item.symbol && (
            <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {item.symbol}
            </Text>
          )}
        </View>
        <Text
          className="text-sm text-gray-600 dark:text-gray-400"
          numberOfLines={1}
          {...webPrivacyProps('private')}
        >
          {item.name}
        </Text>
        <Text className="text-xs text-gray-400" {...webPrivacyProps(['private', 'financial'])}>
          {item.accountName}
          {item.quantity != null && item.quantity !== 0 ? ` · ${formatQuantity(Math.abs(item.quantity))} shares` : ''}
          {item.price != null && item.price !== 0 ? ` @ ${formatCurrency(item.price)}` : ''}
        </Text>
      </View>
      <Money
        weight={500}
        className={`text-sm ${isInflow ? 'text-forest-600 dark:text-green-500' : 'text-gray-900 dark:text-gray-100'}`}
      >
        {formatCurrency(Math.abs(item.amount))}
      </Money>
    </View>
  );
}

function formatActivityDateHeader(dateStr: string): string {
  // dateStr is YYYY-MM-DD; render as "Mon, Jan 5" style without timezone shift.
  const [y, m, d] = dateStr.split('-').map((x) => Number(x));
  if (!y || !m || !d) return dateStr;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export default function HoldingsScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const { width } = useWindowDimensions();
  const params = useLocalSearchParams<{ tab?: string }>();
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<TopTab>(params.tab === 'activity' ? 'activity' : 'holdings');
  const [sort, setSort] = useState<SortMode>('largest');
  const [group, setGroup] = useState<GroupMode>('none');
  const [notesOpen, setNotesOpen] = useState(false);

  // Keep state in sync when the route param changes (e.g. notification re-tap)
  useEffect(() => {
    if (params.tab === 'activity') setTab('activity');
    else if (params.tab === 'holdings') setTab('holdings');
  }, [params.tab]);

  const c = useColors();
  const holdingsQ = trpc.holdings.list.useQuery(undefined, { enabled: isFocused });

  // Activity filters
  const [activityPage, setActivityPage] = useState(1);
  const [activityAccountId, setActivityAccountId] = useState<string>('all');
  const [activityType, setActivityType] = useState<string>('all');
  const activityQ = trpc.investmentTransactions.list.useQuery(
    {
      page: activityPage,
      pageSize: 25,
      ...(activityAccountId !== 'all' ? { accountId: activityAccountId } : {}),
      ...(activityType !== 'all' ? { type: activityType } : {}),
    },
    { enabled: isFocused },
  );
  const accumulatedActivity = useRef<InvestmentTransactionItem[]>([]);
  const lastActivityPage = useRef(0);
  const lastActivityFilterKey = useRef<string>('all|all');

  // Reset pagination when filters change
  const filterKey = `${activityAccountId}|${activityType}`;
  useEffect(() => {
    if (filterKey !== lastActivityFilterKey.current) {
      lastActivityFilterKey.current = filterKey;
      setActivityPage(1);
      accumulatedActivity.current = [];
      lastActivityPage.current = 0;
    }
  }, [filterKey]);

  useEffect(() => {
    if (activityQ.data && activityPage !== lastActivityPage.current) {
      if (activityPage === 1) {
        accumulatedActivity.current = activityQ.data.items;
      } else {
        accumulatedActivity.current = [...accumulatedActivity.current, ...activityQ.data.items];
      }
      lastActivityPage.current = activityPage;
    }
  }, [activityQ.data, activityPage]);

  const activityItems = activityPage === 1 && activityQ.data
    ? activityQ.data.items
    : accumulatedActivity.current;
  const activityTotal = activityQ.data?.total ?? 0;

  const refetchAll = useCallback(async () => {
    setRefreshing(true);
    setActivityPage(1);
    accumulatedActivity.current = [];
    lastActivityPage.current = 0;
    await Promise.allSettled([holdingsQ.refetch(), activityQ.refetch()]);
    setRefreshing(false);
  }, [holdingsQ, activityQ]);

  const portfolio = holdingsQ.data;
  const items = useMemo(() => portfolio?.items ?? [], [portfolio?.items]);
  const summary = portfolio?.summary;
  const isWide = width >= 960;

  const accountOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of items) {
      if (!seen.has(item.accountId)) seen.set(item.accountId, item.accountName);
    }
    return [
      { label: 'All accounts', value: 'all' },
      ...[...seen.entries()].map(([value, label]) => ({ label, value })),
    ];
  }, [items]);

  const flatSortedItems = useMemo(
    () => sortPortfolioHoldings(sort === 'movers' ? filterDailyMarketHoldings(items) : items, sort),
    [items, sort],
  );
  const accountGroups = useMemo(() => groupByAccount(items), [items]);
  const symbolAggregates = useMemo(() => aggregatePortfolioHoldingsBySymbol(items), [items]);

  const activityByDate = useMemo(() => {
    const groups: { date: string; items: InvestmentTransactionItem[] }[] = [];
    for (const item of activityItems) {
      const last = groups[groups.length - 1];
      if (last && last.date === item.date) last.items.push(item);
      else groups.push({ date: item.date, items: [item] });
    }
    return groups;
  }, [activityItems]);

  const setTopTab = useCallback(
    (next: TopTab) => {
      setTab(next);
      router.setParams({ tab: next });
    },
    [router],
  );

  if (holdingsQ.isLoading) {
    return <LoadingSpinner message="Loading your portfolio..." />;
  }

  if (holdingsQ.error) {
    return (
      <ErrorState
        error={holdingsQ.error}
        screen="holdings"
        metadata={{ failedQueries: 'holdings.list' }}
        onRetry={refetchAll}
      />
    );
  }

  if (!summary || items.length === 0) {
    return (
      <>
        <WebHead><title>Portfolio – Per Diem</title></WebHead>
        <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
          <ConnectionErrorBanner onReconnected={refetchAll} />
          <PageContainer>
            <PageHeader title="Portfolio" closeTo="/(app)" />
            <EmptyState
              title="No investment holdings yet"
              message="Connect a brokerage account and let your next sync finish to see your portfolio here."
              actionLabel="Open accounts"
              onAction={() => router.push('/(app)/accounts')}
            />
          </PageContainer>
        </ScrollView>
      </>
    );
  }

  return (
    <>
      <WebHead><title>Portfolio – Per Diem</title></WebHead>
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refetchAll} tintColor={c.primary} />
        }
      >
        <ConnectionErrorBanner onReconnected={refetchAll} />
        <PageContainer className="gap-4">
          <PageHeader title="Portfolio" closeTo="/(app)" />

          {/* Compact summary header */}
          <View className="rounded-[18px] overflow-hidden shadow-card">
            <LinearGradient
              colors={['#173D2A', '#1F5237']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{ padding: 20 }}
            >
              <View className="flex-row items-end justify-between gap-3">
                <View className="flex-1">
                  <Text
                    className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    Portfolio value
                  </Text>
                  <Money
                    weight={500}
                    className="text-[32px] text-white leading-[1.1] mt-1"
                    style={{ letterSpacing: -1 }}
                  >
                    {formatCurrency(summary.totalValue, { decimals: 0 })}
                  </Money>
                  {summary.dayChangeAmount != null && (
                    <Money
                      weight={500}
                      className="text-[13px] mt-1.5"
                      style={{
                        color: summary.dayChangeAmount < 0
                          ? 'rgba(255,160,140,0.9)'
                          : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {formatCurrencyChange(summary.dayChangeAmount)}
                      {summary.dayChangePercent != null ? ` (${formatSignedPercent(summary.dayChangePercent)})` : ''}{' '}
                      today
                    </Money>
                  )}
                </View>
                <View className="items-end gap-0.5">
                  <Text className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Holdings
                  </Text>
                  <Text className="text-white text-base font-semibold">{summary.holdingsCount}</Text>
                  <Text className="text-[10px] uppercase tracking-wide mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Accounts
                  </Text>
                  <Text className="text-white text-base font-semibold">{summary.accountCount}</Text>
                </View>
              </View>
              {summary.totalGainLossAmount != null && (
                <Text
                  className="text-[11px] mt-3 pt-2 border-t"
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                    borderColor: 'rgba(255,255,255,0.12)',
                  }}
                >
                  All-time {formatCurrencyChange(summary.totalGainLossAmount)}
                  {summary.totalGainLossPercent != null ? ` (${formatSignedPercent(summary.totalGainLossPercent)})` : ''}
                </Text>
              )}
            </LinearGradient>
          </View>

          {/* Tabs: Holdings | Activity */}
          <View className="items-start">
            <SegmentedControl
              options={TAB_OPTIONS}
              value={tab}
              onChange={setTopTab}
              size="md"
            />
          </View>

          {/* Collapsible portfolio notes */}
          {summary.notes.length > 0 && (
            <Pressable
              onPress={() => setNotesOpen((v) => !v)}
              className="flex-row items-center gap-2 -mt-1"
            >
              <Ionicons
                name={notesOpen ? 'chevron-down' : 'chevron-forward'}
                size={12}
                color="#6B7280"
              />
              <Text className="text-xs text-gray-500">
                {summary.notes.length} {summary.notes.length === 1 ? 'note' : 'notes'} about this portfolio
              </Text>
            </Pressable>
          )}
          {notesOpen && summary.notes.length > 0 && (
            <View className="gap-2 -mt-2">
              {summary.notes.map((note) => (
                <View key={note} className="rounded-xl bg-gray-50 dark:bg-gray-800 px-3 py-2">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">{note}</Text>
                </View>
              ))}
            </View>
          )}

          {tab === 'holdings' && (
            <Card>
              <View className={`mb-3 gap-3 ${isWide ? 'flex-row items-end justify-between flex-wrap' : ''}`}>
                <View className="flex-1 min-w-[200px]">
                  <Text className="text-base font-semibold text-gray-800 dark:text-gray-200">
                    {group === 'symbol' ? 'Positions by symbol' : group === 'account' ? 'Positions by account' : sort === 'movers' ? 'Daily movers' : 'Largest positions'}
                  </Text>
                  <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {group === 'symbol'
                      ? 'Same ticker held in multiple accounts is consolidated into a single row.'
                      : group === 'account'
                        ? 'Each brokerage account collapses so you can focus on one at a time.'
                        : sort === 'movers'
                          ? 'Sorted by absolute daily movement when today’s price data is available.'
                          : 'Sorted by current portfolio value so the biggest positions stay obvious.'}
                  </Text>
                </View>
                <View className="flex-row flex-wrap gap-2 items-center">
                  <View>
                    <Text className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Group</Text>
                    <SegmentedControl options={GROUP_OPTIONS} value={group} onChange={setGroup} />
                  </View>
                  {group !== 'symbol' && (
                    <View>
                      <Text className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Sort</Text>
                      <SegmentedControl options={SORT_OPTIONS} value={sort} onChange={setSort} />
                    </View>
                  )}
                </View>
              </View>

              {group === 'account' ? (
                <View className="gap-3">
                  {accountGroups.map((g, idx) => (
                    <AccountGroupSection
                      key={g.accountId}
                      group={g}
                      sort={sort}
                      defaultExpanded={idx === 0 || accountGroups.length <= 2}
                    />
                  ))}
                </View>
              ) : group === 'symbol' ? (
                <View className="gap-3">
                  {symbolAggregates.map((agg) => (
                    <SymbolAggregateRow key={agg.key} agg={agg} />
                  ))}
                </View>
              ) : (
                <View className="gap-3">
                  {flatSortedItems.map((item) => (
                    <HoldingRow key={item.id} item={item} />
                  ))}
                </View>
              )}
            </Card>
          )}

          {tab === 'activity' && (
            <Card>
              <View className={`mb-3 gap-3 ${isWide ? 'flex-row items-end justify-between flex-wrap' : ''}`}>
                <SectionHeader title="Investment activity" className="mb-0 flex-1" />
                <View className="flex-row flex-wrap gap-2">
                  <View className="min-w-[160px]">
                    <Select
                      size="sm"
                      value={activityAccountId}
                      options={accountOptions}
                      onChange={setActivityAccountId}
                    />
                  </View>
                  <View className="min-w-[140px]">
                    <Select
                      size="sm"
                      value={activityType}
                      options={[...ACTIVITY_TYPE_OPTIONS]}
                      onChange={setActivityType}
                    />
                  </View>
                </View>
              </View>

              {activityQ.isLoading && activityPage === 1 ? (
                <Text className="text-sm text-gray-500">Loading activity...</Text>
              ) : activityItems.length === 0 ? (
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  No investment activity matches these filters. Try clearing the account or type filter.
                </Text>
              ) : (
                <View>
                  {activityByDate.map((group) => (
                    <View key={group.date} className="mb-2">
                      <Text className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">
                        {formatActivityDateHeader(group.date)}
                      </Text>
                      {group.items.map((txn) => (
                        <ActivityRow key={txn.id} item={txn} />
                      ))}
                    </View>
                  ))}
                  {activityItems.length < activityTotal && (
                    <Pressable
                      className="mt-3 items-center py-2"
                      onPress={() => setActivityPage((p) => p + 1)}
                      disabled={activityQ.isFetching}
                    >
                      <Text className="text-sm font-medium text-green-600">
                        {activityQ.isFetching ? 'Loading...' : 'Load more'}
                      </Text>
                    </Pressable>
                  )}
                  <Text className="text-[11px] text-gray-400 mt-3 text-center">
                    Showing {activityItems.length} of {activityTotal}
                  </Text>
                </View>
              )}
            </Card>
          )}

          <Pressable onPress={() => router.push('/(app)/accounts')} className="py-2">
            <Text className="text-xs text-center text-gray-500 dark:text-gray-400">
              Need to reconnect or add an investment account? <Text className="text-green-600 dark:text-green-500 font-medium">Open accounts</Text>
            </Text>
          </Pressable>
        </PageContainer>
      </ScrollView>
    </>
  );
}
